import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, organizationApi, systemApi, auditApi } from '@/utils/api';
import type { AdminUser, CreditAdjustment } from '@/types';

// Query Keys
export const queryKeys = {
  users: ['users'],
  user: (uid: string) => ['users', uid],
  organizations: ['organizations'],
  organization: (orgId: string) => ['organizations', orgId],
  organizationMembers: (orgId: string) => ['organizations', orgId, 'members'],
  systemHealth: ['system', 'health'],
  analytics: ['analytics'],
  auditLogs: ['audit-logs'],
};

// User Hooks
export const useUsers = (params?: {
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useQuery({
    queryKey: [...queryKeys.users, params],
    queryFn: () => userApi.getUsers(params),
    staleTime: 30 * 1000, // 30 seconds
    // Keep the previous page on screen while the next one loads.
    placeholderData: (previousData) => previousData,
  });
};

export const useUser = (uid: string) => {
  return useQuery({
    queryKey: queryKeys.user(uid),
    queryFn: () => userApi.getUser(uid),
    enabled: !!uid,
  });
};

export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uid,
      status,
    }: {
      uid: string;
      status: AdminUser['status'];
    }) => userApi.updateUserStatus(uid, status),
    onSuccess: (_, { uid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(uid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};

export const useAdjustCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uid,
      adjustment,
    }: {
      uid: string;
      adjustment: CreditAdjustment;
    }) => userApi.adjustCredits(uid, adjustment),
    onSuccess: (_, { uid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(uid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
};

// Organization Hooks
export const useOrganizations = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  plan?: string;
  type?: string;
}) => {
  return useQuery({
    queryKey: [...queryKeys.organizations, params],
    queryFn: () => organizationApi.getOrganizations(params),
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useOrganization = (orgId: string) => {
  return useQuery({
    queryKey: queryKeys.organization(orgId),
    queryFn: () => organizationApi.getOrganization(orgId),
    enabled: !!orgId,
  });
};

export const useOrganizationMembers = (orgId: string) => {
  return useQuery({
    queryKey: queryKeys.organizationMembers(orgId),
    queryFn: () => organizationApi.getOrganizationMembers(orgId),
    enabled: !!orgId,
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, updates }: { orgId: string; updates: any }) =>
      organizationApi.updateOrganization(orgId, updates),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization(orgId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
};

export const useAdjustOrganizationCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgId,
      adjustment,
    }: {
      orgId: string;
      adjustment: CreditAdjustment;
    }) => organizationApi.adjustOrganizationCredits(orgId, adjustment),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization(orgId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
};

// System Hooks
export const useSystemHealth = (refetchInterval?: number) => {
  return useQuery({
    queryKey: queryKeys.systemHealth,
    queryFn: () => systemApi.getSystemHealth(),
    refetchInterval: refetchInterval || 60 * 1000,
    staleTime: 30 * 1000,
  });
};

export const useAnalytics = (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: [...queryKeys.analytics, params],
    queryFn: () => systemApi.getAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Audit Logs Hooks
export const useAuditLogs = (params?: {
  page?: number;
  limit?: number;
  action?: string;
  targetUserId?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: [...queryKeys.auditLogs, params],
    queryFn: () => auditApi.getAuditLogs(params),
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useExportAuditLogs = () => {
  return useMutation({
    mutationFn: (params?: {
      format?: 'csv' | 'json';
      startDate?: string;
      endDate?: string;
    }) => auditApi.exportAuditLogs(params),
  });
};

/**
 * Dashboard system health. Polls in the foreground only: the admin-service
 * rate limit is per caller, and a background tab polling every few seconds
 * was most of the budget (SPRITE-1468).
 */
export const useSystemHealthRealtime = (
  enabled: boolean = true,
  interval: number = 60 * 1000
) => {
  return useQuery({
    queryKey: queryKeys.systemHealth,
    queryFn: () => systemApi.getSystemHealth(),
    refetchInterval: enabled ? interval : false,
    staleTime: 30 * 1000,
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    retry: (failureCount, error: any) => {
      if (error?.status === 429) return false;
      if (error?.status >= 500) return failureCount < 5;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

// Analytics Hook with Real-time Updates
export const useAnalyticsRealtime = (
  params?: {
    startDate?: string;
    endDate?: string;
  },
  enabled: boolean = true,
  interval: number = 5 * 60 * 1000
) => {
  return useQuery({
    queryKey: [...queryKeys.analytics, params],
    queryFn: () => systemApi.getAnalytics(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: enabled ? interval : false,
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep previous data while refetching for smooth UX
    placeholderData: (previousData) => previousData,
  });
};

// Audit Logs Hook with Real-time Updates
export const useAuditLogsRealtime = (
  params?: {
    page?: number;
    limit?: number;
    action?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
  },
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...queryKeys.auditLogs, params],
    queryFn: () => auditApi.getAuditLogs(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: enabled ? 60 * 1000 : false, // 1 minute
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep previous data for smooth pagination
    placeholderData: (previousData) => previousData,
  });
};

/** Run a bulk request in chunks, collecting per-chunk failures. */
async function runInChunks(
  ids: string[],
  send: (chunk: string[]) => Promise<unknown>,
  onProgress?: (progress: {
    current: number;
    total: number;
    percentage: number;
  }) => void
) {
  const chunkSize = 10;
  let completed = 0;
  const errors: Array<{ id: string; error: string }> = [];

  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    try {
      await send(chunk);
      completed += chunk.length;
      onProgress?.({
        current: completed,
        total: ids.length,
        percentage: Math.round((completed / ids.length) * 100),
      });
    } catch (error) {
      chunk.forEach((id) => {
        errors.push({
          id,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      });
    }
  }

  return {
    success: completed,
    failed: ids.length - completed,
    errors,
    total: ids.length,
  };
}

// Bulk Operations with Progress Tracking
export const useBulkUpdateUsersWithProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userIds,
      updates,
      onProgress,
    }: {
      userIds: string[];
      updates: Record<string, unknown>;
      onProgress?: (progress: {
        current: number;
        total: number;
        percentage: number;
      }) => void;
    }) =>
      runInChunks(
        userIds,
        (chunk) => userApi.bulkUpdateUsers(chunk, updates),
        onProgress
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};

export const useBulkUpdateOrganizationsWithProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgIds,
      updates,
      onProgress,
    }: {
      orgIds: string[];
      updates: Record<string, any>;
      onProgress?: (progress: {
        current: number;
        total: number;
        percentage: number;
      }) => void;
    }) =>
      runInChunks(
        orgIds,
        (chunk) => organizationApi.bulkUpdateOrganizations(chunk, updates),
        onProgress
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
};
